import React from 'react'
import { Checkbox, Typography } from 'antd'
import { neutralColors, spacing } from '@vacationtracker/shared/constants/ThemeSetting'

const { Text } = Typography

export interface CheckboxFieldProps {
  checked?: boolean
  onChange?: (checked: boolean) => void
  label?: React.ReactNode
  description?: React.ReactNode
  disabled?: boolean
  indeterminate?: boolean
  /** Names the checkbox for assistive tech when there is no visible `label`, e.g. in a table row. */
  ariaLabel?: string
}

/**
 * Labeled antd Checkbox. Own label/description row (not Form.Item-bound), same pattern
 * as `SwitchField`, so it works fully controlled outside a Form context too.
 */
export function CheckboxField({ checked, onChange, label, description, disabled = false, indeterminate = false, ariaLabel }: CheckboxFieldProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.xs }}>
      <Checkbox aria-label={ariaLabel} checked={checked} onChange={(e) => onChange?.(e.target.checked)} disabled={disabled} indeterminate={indeterminate} />
      {(label || description) && (
        <div>
          {label && <Text style={{ color: neutralColors.text }}>{label}</Text>}
          {description && (
            <div>
              <Text style={{ color: neutralColors.textSecondary }}>{description}</Text>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export interface CheckboxGroupFieldOption {
  label: React.ReactNode
  value: string | number
}

export interface CheckboxGroupFieldProps {
  options: CheckboxGroupFieldOption[]
  value?: (string | number)[]
  onChange?: (value: (string | number)[]) => void
  label?: React.ReactNode
  disabled?: boolean
}

/**
 * Wraps antd `Checkbox.Group` with an `options` array, matching the two real
 * call sites that build a work-week picker this way (`components/location-form`,
 * `routes/Notifications/NotificationsWeeklyDailyFormPage`) instead of a single checkbox.
 */
export function CheckboxGroupField({ options, value, onChange, label, disabled = false }: CheckboxGroupFieldProps) {
  return (
    <div>
      {label && (
        <div style={{ marginBottom: spacing.xxs }}>
          <Text style={{ color: neutralColors.textSecondary }}>{label}</Text>
        </div>
      )}
      <Checkbox.Group options={options} value={value} onChange={onChange} disabled={disabled} />
    </div>
  )
}

export default CheckboxField
